import RafThrottle from '@utilities/raf-throttle';
import { html } from '@utilities/dom-elements';
import Events from '@utilities/events';

const HEADER_HIDDEN_CLASS = 'header--is-hidden';
const HEADER_HIDE_SMALL_LOGO_CLASS = 'header--hide-small-logo';
const HEADER_HIDE_GLOW_CLASS = 'header--hide-glow';
const HEADER_HIDE_FLYOUT_CLASS = 'header--hide-flyout';
const HEADER_SCROLLUP_CLASS = 'header--is-scrolled-up';
const HEADER_VISIBLITY_NAMESPACE = 'headerVisibility';
const HEADER_TRANSFORM_MIN_SCROLL = 80;
const HEADER_TRANSFORM_THRESHOLD = 0;

const HOOK_HERO_SECTION_ANIMATION = '[js-hook-hero-animation]';
const HOOK_HERO_MEDIA = '[js-hook-hero-media]';
const HOOK_LARGE_LOGO_CONTAINER = '[js-hook-large-logo-container]';
const HOOK_HEADER_HERO = '[js-hook-header-hero]'
const HOOK_BUTTON_CLOSE_USPS = '[js-hook-button-usps-close]'
const HOOK_TOP_WRAPPER = '[js-hook-top-wrapper]';

const CLASS_SEARCH_HTML = 'search--is-open';
const CLASS_SEARCH_INPUT_HTML = 'search--is-in-focus';
const CLASS_MENU_FLYOUT_OPEN = 'header--mega-menu-open';

const HIDDEN_CLASS = 'u-hidden';
const HEADER_HIDE_USPS = 'header--hide-usps';
const HEADER_HIDE_TOP_WRAPPER = 'header--top-wrapper-is-hidden';

let LAST_SCROLL_TOP = 0;

class Header {
  constructor(element) {
    this.header = element;
    this.largeLogoContainer = document.querySelector(HOOK_LARGE_LOGO_CONTAINER);
    this.largeLogo = this.largeLogoContainer?.querySelector('svg');
    this.minScroll = this.header.dataset.minScroll ? this.header.dataset.minScroll : HEADER_TRANSFORM_MIN_SCROLL;
    this.offset = 0;
    this.headerHero = document.querySelector(HOOK_HEADER_HERO);
    this.heroSection = document.querySelector(HOOK_HERO_SECTION_ANIMATION);
    this.heroMedia = document.querySelector(HOOK_HERO_MEDIA);
    this.windowWidth = window.innerWidth;
    this.buttonCloseUsps = document.querySelector(HOOK_BUTTON_CLOSE_USPS);
    this.topWrapper = document.querySelector(HOOK_TOP_WRAPPER);

    if (this.heroSection) {
      this.heroSectionHeight = this.heroSection.getBoundingClientRect().bottom;
    }

    this.headerTransformMinScroll = this.largeLogo ? this.largeLogo.getBoundingClientRect().height + this.minScroll : this.minScroll;
    // this.dropdowns = [...this.header.querySelectorAll(HOOK_DROPDOWN_CONTAINER)]

    this.bindEvents();
  }

  /**
   * Binds events
   */
  bindEvents() {

    this.showAndHideHeader();
    RafThrottle.set([
      {
        element: window,
        event: 'scroll',
        namespace: HEADER_VISIBLITY_NAMESPACE,
        fn: () => this.showAndHideHeader(),
      },
    ]);

    if (this.heroSection) {
      RafThrottle.set([
        {
          element: window,
          event: 'scroll',
          namespace: `${HEADER_VISIBLITY_NAMESPACE}Snap`,
          fn: () => this.toggleWidthClass(),
        },
      ]);
    }

    if (this.largeLogo) {
      Events.$on('search::input-open', () => this.headerOpened());
      Events.$on('modal[modal-search]::open', () => this.headerOpened());
      Events.$on('modal[modal-mega-menu]::open', () => this.headerOpened());

      this.showAndHideLogo();
      RafThrottle.set([
        {
          element: window,
          event: 'scroll',
          namespace: `${HEADER_VISIBLITY_NAMESPACE}Logo`,
          fn: () => this.showAndHideLogo(),
        },
      ]);
    }

    Events.$on('header::show', () => {
      this.header.classList.remove(HEADER_HIDDEN_CLASS);
    });

    Events.$on('header::hide', () => {
      this.header.classList.add(HEADER_HIDDEN_CLASS);
    });

    if (this.headerHero) {
      html.classList.add('header-hero');
    }

    if (this.buttonCloseUsps) {
      this.buttonCloseUsps.addEventListener('click', () => {
        this.topWrapper.classList.add(HIDDEN_CLASS);
        html.classList.add(HEADER_HIDE_USPS);
      });
    } else if (!this.buttonCloseUsps) {
      html.classList.add(HEADER_HIDE_TOP_WRAPPER);
    }
  }

  toggleWidthClass() {
    const scrollTop = window.scrollY;
    const fullWidth = 100;
    const animationSpeedEase = 50;
    const heroSectionOffsetTop = scrollTop - this.heroSection.offsetTop;
    const heroSectionOffsetHeight = this.heroSection.offsetHeight;
    const heroSectionOffsetWidth = this.heroSection.offsetWidth;
    const calculateSpeed = heroSectionOffsetWidth / heroSectionOffsetHeight;
    const calculateWidth = `${fullWidth - heroSectionOffsetTop * calculateSpeed / animationSpeedEase}%`;

    if (this.heroSection) {
      if (heroSectionOffsetTop > 0) {
        this.heroMedia.style.width = calculateWidth;
      } else {
        this.heroMedia.style.width = `${fullWidth}%`;
      }
    }
  }

  headerOpened() {
    const scrollTop = window.scrollY;
    const scrollDuration = 200;

    if( scrollTop < this.headerTransformMinScroll ) {
      html.classList.add(HEADER_HIDE_FLYOUT_CLASS);
      Events.$trigger('scroll-to::scroll', {
        data: {
          target: this.header,
          offset: 0,
          duration: scrollDuration,
        },
      });

      setTimeout(() => {
        html.classList.remove(HEADER_HIDE_FLYOUT_CLASS);
      }, scrollDuration);
    }
  }

  showAndHideLogo() {
    const scrollTop = window.scrollY;

    this.largeLogoContainer.style.height = `${this.getLogoContainerHeight()}px`;

    // Set small logo visibility
    if (scrollTop < this.headerTransformMinScroll * 0.4) {
      html.classList.add(HEADER_HIDE_SMALL_LOGO_CLASS);
    } else {
      html.classList.remove(HEADER_HIDE_SMALL_LOGO_CLASS);
    }

    // Set glow visibility
    if (scrollTop < this.headerTransformMinScroll * 0.9) {
      html.classList.add(HEADER_HIDE_GLOW_CLASS);
    } else {
      html.classList.remove(HEADER_HIDE_GLOW_CLASS);
    }
  }

  getLogoContainerHeight() {
    const height = this.header.getBoundingClientRect().top;

    if (height < 0) return 0;
    return parseInt(height, 10);
  }

  /**
   * Check how far the page has scrolled and set header visibility
   */
  showAndHideHeader() {
    const scrollTop = window.scrollY;
    const scrollDiff = LAST_SCROLL_TOP - scrollTop;

    if (
      html.classList.contains(CLASS_SEARCH_HTML) ||
      html.classList.contains(CLASS_SEARCH_INPUT_HTML) ||
      html.classList.contains(CLASS_MENU_FLYOUT_OPEN)
    )
      return;

    if (scrollTop === 0) {
      // At the top of the page
      html.classList.remove(HEADER_HIDDEN_CLASS);
      html.classList.remove(HEADER_SCROLLUP_CLASS);
    } else if (scrollTop < this.header.offsetHeight) {
      html.classList.remove(HEADER_SCROLLUP_CLASS);
    } else if (
      scrollTop > LAST_SCROLL_TOP &&
      scrollTop > HEADER_TRANSFORM_THRESHOLD &&
      scrollTop > this.headerTransformMinScroll
    ) {
      // Scrolling down, passed threshold
      html.classList.add(HEADER_HIDDEN_CLASS);
      html.classList.remove(HEADER_SCROLLUP_CLASS);

      // this.handlePostHideEvents()
    } else if (scrollDiff > HEADER_TRANSFORM_THRESHOLD) {
      // Scrolling up, passed threshold
      html.classList.remove(HEADER_HIDDEN_CLASS);
      html.classList.add(HEADER_SCROLLUP_CLASS);
    }

    LAST_SCROLL_TOP = scrollTop <= 0 ? 0 : scrollTop; // For Mobile or negative scrolling
  }

  /**
   * Handles all events after the header is hidden
   */
  // handlePostHideEvents() {
  //   Events.$trigger(`modal[modal-mega-menu]::close`);
  //
  //   this.dropdowns.forEach((item) => {
  //     item.querySelector(HOOK_DROPDOWN_ELEMENT).hidden = true;
  //     item.querySelector(HOOK_DROPDOWN_BUTTON).setAttribute('aria-expanded', false);
  //   });
  // }
}

export default Header;
